import * as Highcharts from 'highcharts';

export class HighchartsThemeEngine {

	static apply(primaryColor: string) {
		const colors = this.generatePalette(primaryColor);

		Highcharts.setOptions({
			colors
		});
	}

	static generatePalette(primaryColor: string): string[] {

		const hsl = this.hexToHsl(primaryColor);

		return [
			primaryColor, // 1. brand color

			// 2. always strong contrast
			this.getContrast(primaryColor),

			// 3–4. primary variations
			this.shadeColor(primaryColor, -25),
			this.shadeColor(primaryColor, 25),

			// 5–7. hue-shifted accents (works for ANY primary color)
			this.hslToHex((hsl.h + 40) % 360, hsl.s, hsl.l),
			this.hslToHex((hsl.h + 140) % 360, hsl.s, hsl.l),
			this.hslToHex((hsl.h + 220) % 360, hsl.s, hsl.l),

			// 8. neutral fallback
			'#6C757D'
		];
	}

	static getContrast(hex: string): string {
		const hsl = this.hexToHsl(hex);
		return this.hslToHex((hsl.h + 180) % 360, hsl.s, hsl.l);
	}

	static hexToHsl(hex: string) {
		const r = parseInt(hex.slice(1, 3), 16) / 255;
		const g = parseInt(hex.slice(3, 5), 16) / 255;
		const b = parseInt(hex.slice(5, 7), 16) / 255;

		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);

		let h = 0;
		let s = 0;
		let l = (max + min) / 2;

		if (max !== min) {
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

			if (max === r) {
				h = (g - b) / d + (g < b ? 6 : 0);
			} else if (max === g) {
				h = (b - r) / d + 2;
			} else {
				h = (r - g) / d + 4;
			}

			h *= 60;
		}

		return { h, s, l };
	}

	static hslToHex(h: number, s: number, l: number): string {
		const c = (1 - Math.abs(2 * l - 1)) * s;
		const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
		const m = l - c / 2;

		let r = 0, g = 0, b = 0;

		if (h < 60) {
			r = c; g = x; b = 0;
		} else if (h < 120) {
			r = x; g = c; b = 0;
		} else if (h < 180) {
			r = 0; g = c; b = x;
		} else if (h < 240) {
			r = 0; g = x; b = c;
		} else if (h < 300) {
			r = x; g = 0; b = c;
		} else {
			r = c; g = 0; b = x;
		}

		const toHex = (v: number) =>
			Math.round((v + m) * 255)
				.toString(16)
				.padStart(2, '0');

		return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
	}

	static shadeColor(color: string, percent: number): string {
		const num = parseInt(color.slice(1), 16);
		const amt = Math.round(2.55 * percent);

		const r = Math.min(255, Math.max(0, (num >> 16) + amt));
		const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amt));
		const b = Math.min(255, Math.max(0, (num & 0xff) + amt));

		return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b)
			.toString(16)
			.slice(1)}`;
	}
}